/**
 * Minimal hash-based router.
 *
 * A hash router (`#/path`) is used so the SPA works as static files behind
 * CloudFront with no server-side rewrite rules. Routes are matched by a simple
 * prefix + segment scheme; the app registers a single change handler and
 * re-renders on `hashchange`. Hash routing also means a 401-driven redirect to
 * Passcode entry is a pure client concern (Req 1.6).
 */

/** A parsed route: the leading segment and any following segments. */
export interface Route {
  /** The full normalized path, e.g. `/crops/corn`. */
  path: string;
  /** Path segments with the empty root normalized away, e.g. `["crops","corn"]`. */
  segments: string[];
}

/** Parse the current `location.hash` into a {@link Route}. */
export function currentRoute(): Route {
  const raw = window.location.hash.replace(/^#/, "");
  const path = raw.length === 0 ? "/" : raw;
  const segments = path.split("/").filter((s) => s.length > 0);
  return { path: path.startsWith("/") ? path : `/${path}`, segments };
}

/** Navigate to a hash path (no-op if already there). */
export function navigate(path: string): void {
  const target = path.startsWith("#") ? path : `#${path}`;
  if (window.location.hash === target) {
    // Force a re-render even when the hash is unchanged.
    window.dispatchEvent(new HashChangeEvent("hashchange"));
    return;
  }
  window.location.hash = target;
}

/** Subscribe to route changes; returns an unsubscribe function. */
export function onRouteChange(handler: () => void): () => void {
  window.addEventListener("hashchange", handler);
  return () => window.removeEventListener("hashchange", handler);
}
